import { DeepLinkController } from "@cntrl/native";

/**
 * The artist SPA's top-level route segments — the same-origin allowlist the push/universal-link sanitizer
 * checks. `""` covers the index route `/` (dashboard). A tapped `route` whose first segment is not
 * here is dropped; keep this in step with the router's route tree as surfaces land. It is defense-in-depth on
 * top of the structural same-origin check in {@link DeepLinkController} — an off-list route can only ever be a
 * same-origin path, never a cross-origin redirect.
 */
export const APP_ROUTE_ROOTS: ReadonlySet<string> = new Set([
  "", // "/" — dashboard
  "login",
  "forgot-password",
  "reset",
  "join", // /join?token= — every seat invite's link (ART-239); a tapped invite email on the phone opens here
  "booking", // /booking, /booking/shows/:id/guests
  "shows", // /shows/:id/guests (the guest-scanned push deep-link target)
  "scan", // /scan?token= — the NFC-sticker UNIVERSAL LINK; AASA routes a tapped sticker here, and the
  //          deep-link sanitizer must accept it as a same-origin target (it opens the in-app scan/toggle landing)
  "road-cases", // /road-cases — the NFC road-case authoring screen (reached from the Case-Checklist widget)
  // Notification + push deep-link targets — the roots the dispatcher writes onto notification `route`s
  // each a real router route. Kept in step with the router tree so a notification tap is never
  // silently dropped; every entry is still same-origin-checked structurally, this is the leading-segment
  // allowlist on top. NB: task_assigned and offer/opportunity notifications route to the DASHBOARD ("" root —
  // `/?task=:id` routed modal, and the money-free Opportunities widget), so no `tasks`/`opportunities` root
  // is needed.
  "dos", // dos_load_in / dos_showtime → /dos?show=:id (the Day-of-Show detail, NOT the desktop booking page)
  "messages", // message_* → /messages/:threadId
  "contacts", // friend_request / team_removed → /contacts
  "calendar", // the read-only calendar (support_added itself routes to /booking?tab=confirmed)
  "schedule", // the calendar's pre-ART-305 path, redirected to /calendar
  "socials", // post_spike → /socials (the Social command center; the dispatcher's post_spike route)
  "roster", // roster_* → /roster (org home; /roster/artists lands in Phase 7)
  "partner", // partner → /partner (the Personal Viewer surface, the dispatcher's partner-kind route)
  "notifications", // /notifications (the center's own route)
  "antenna",
  "settings",
  "profile",
  "onboarding",
]);

/** The app's deep-link controller: sanitizes taps against {@link APP_ROUTE_ROOTS}, parks cold-launch taps
 * until Bootstrap activates it with the router navigator. */
export function createDeepLinkController(): DeepLinkController {
  return new DeepLinkController(APP_ROUTE_ROOTS);
}
